import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import { Link } from "react-router-dom";
import { Icon } from "@/lib/icons";

const EASE = [0.16, 1, 0.3, 1];

/* Scroll-triggered fade/slide reveal */
export function Reveal({ children, delay = 0, y = 30, className = "", as = "div" }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </MotionTag>
  );
}

/* Masked, line-by-line kinetic text reveal (for hero) */
export function LineReveal({ lines, className = "", lineClass = "", delayStart = 0.1 }) {
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            className={`block ${lineClass}`}
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, ease: EASE, delay: delayStart + i * 0.12 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

/* Animated number counter, triggered in view */
export function Counter({ value, prefix = "", suffix = "", decimals = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, value, {
      duration: 2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => {
        setDisplay(
          decimals > 0
            ? v.toFixed(decimals)
            : Math.round(v).toLocaleString("en-IN")
        );
      },
    });
    return () => controls.stop();
  }, [inView, value, mv, decimals]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

export function Overline({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-teal ${className}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-teal shadow-[0_0_12px_#00D4AA]" />
      {children}
    </span>
  );
}

/* Ambient radial glow orb for cinematic depth */
export function GlowOrb({ className = "", color = "rgba(0,212,170,0.22)" }) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute rounded-full blur-[120px] ${className}`}
      style={{ background: color }}
    />
  );
}

/* Section heading block */
export function SectionHeading({ overline, title, sub, center = false, className = "" }) {
  return (
    <div className={`${center ? "mx-auto text-center max-w-3xl" : "max-w-3xl"} ${className}`}>
      {overline && (
        <Reveal>
          <Overline>{overline}</Overline>
        </Reveal>
      )}
      <Reveal delay={0.05}>
        <h2 className="mt-5 text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05] text-white">
          {title}
        </h2>
      </Reveal>
      {sub && (
        <Reveal delay={0.1}>
          <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">{sub}</p>
        </Reveal>
      )}
    </div>
  );
}

/* Primary / secondary buttons that can be links or buttons */
export function PillButton({ to, href, children, variant = "primary", icon = "ArrowRight", testid, onClick, type }) {
  const base =
    "group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold tracking-wide transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 focus:ring-offset-navy";
  const styles = {
    primary:
      "bg-teal text-navy hover:bg-teal-glow shadow-[0_0_24px_rgba(0,212,170,0.35)] hover:shadow-[0_0_36px_rgba(26,232,189,0.55)]",
    secondary:
      "border border-teal/50 text-white hover:bg-teal/10 hover:border-teal",
    ghost: "text-slate-300 hover:text-white",
  };
  const cls = `${base} ${styles[variant]}`;
  const inner = (
    <>
      {children}
      {icon && (
        <Icon
          name={icon}
          className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
        />
      )}
    </>
  );
  if (to) return <Link to={to} className={cls} data-testid={testid}>{inner}</Link>;
  if (href)
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid}>
        {inner}
      </a>
    );
  return (
    <button type={type || "button"} onClick={onClick} className={cls} data-testid={testid}>
      {inner}
    </button>
  );
}

/* Grid backdrop for section depth */
export function GridBackdrop({ className = "" }) {
  return <div aria-hidden className={`absolute inset-0 grid-bg radial-fade opacity-60 ${className}`} />;
}
